import type { ReactNode } from 'react';
import styles from './dashboardCard.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { MockPill } from '@components/ui/MockPill/MockPill';

/* Shared shell for every card on the Dashboard. Locks in the visual
   constants (white surface, hairline border, soft shadow, --r-3xl
   radius, header underline) so a single edit changes every card.
   Position and size are owned by react-grid-layout (the card fills its
   grid cell via height:100%) — the shell carries no layout variants.

   Slots:
     - `mock` — render the MOCK pill next to the title.
     - `titleIcon` — small glyph rendered before the title text.
     - `headerRight` — slot for legend, "more" menu, "N new today", etc.
     - `subtitle` — secondary copy beneath the title.
     - `bodyClassName` — escape hatch when a card needs to style its
       body differently (e.g. activities feed grows its list to fill
       the cell). Scroll/flex sizing comes from the shared bodyRegion
       class regardless.
*/

/** `compact` is the default uppercase fs-2xs tracked title used by
   every command-center card. `prose` is the sentence-case fs-md
   treatment used by the AI Agent card. */
export type CardTitleAppearance = 'compact' | 'prose';

interface IDashboardCardProps {
  mock?: boolean;
  title: ReactNode;
  titleAppearance?: CardTitleAppearance;
  titleIcon?: ReactNode;
  subtitle?: ReactNode;
  /** Render the subtitle on the same row as the title (single-line header)
      instead of stacked beneath it. Used by height-constrained cards. */
  subtitleInline?: boolean;
  /** Extra content in the header text column, below the subtitle and above the
      header's hairline — e.g. a segmented control that scopes the card body. */
  headerExtra?: ReactNode;
  headerRight?: ReactNode;
  bodyClassName?: string;
  children: ReactNode;
}

export function DashboardCard({
  mock = false,
  title,
  titleAppearance = 'compact',
  titleIcon,
  subtitle,
  subtitleInline = false,
  headerExtra,
  headerRight,
  bodyClassName,
  children,
}: IDashboardCardProps) {
  const titleCls = [
    styles.title,
    titleAppearance === 'prose' ? styles.titleProse : '',
  ].filter(Boolean).join(' ');
  const headerTextCls = subtitleInline
    ? `${styles.headerText} ${styles.headerTextInline}`
    : styles.headerText;

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <div className={headerTextCls}>
          <h2 className={titleCls}>
            {titleIcon && <span className={styles.titleIcon}>{titleIcon}</span>}
            {title}
            {mock && <MockPill />}
          </h2>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          {headerExtra}
        </div>
        {headerRight && <div className={styles.headerRight}>{headerRight}</div>}
      </header>
      <ScrollArea
        orientation="both"
        className={styles.bodyRegion}
        viewportClassName={bodyClassName ?? styles.body}
      >
        {children}
      </ScrollArea>
    </section>
  );
}
